import Link from 'next/link';

import { cn } from '@kit/ui/utils';

import type { MarketingLocale } from '~/lib/marketing/site';

import type { DemoKey } from '../_lib/solutions.content';
import { AudienceVisual } from './audience-visual';
import { ScheduleDemoButton } from './schedule-demo-button';
import { ScheduleDemoPaths } from './schedule-demo-paths';

/*
 * Shared building blocks for marketing content pages (/faq, /compare/*,
 * /booking-software/*, /customers). Server-component friendly, no hooks.
 *
 * Palette = askflow-brand.css primitives:
 *   ink #202020 · graphite #2b2b2b · sand #ede8e2 · bone #f7f6f4
 *   smoke #dcd7cf · sky #e6f0ff · blue #a9c8ff · mint #ddf5ec
 * Typography follows the flow marketing pages: Roboto + Roboto Mono labels.
 */

const SECTION_BG = {
  ink: 'bg-[#202020] text-white',
  sand: 'bg-[#ede8e2] text-[#202020]',
  bone: 'bg-[#f7f6f4] text-[#202020]',
  white: 'bg-white text-[#202020]',
} as const;

export function Section({
  bg = 'bone',
  className,
  children,
  id,
}: React.PropsWithChildren<{
  bg?: keyof typeof SECTION_BG;
  className?: string;
  id?: string;
}>) {
  return (
    <section
      id={id}
      className={cn(
        'py-12 md:py-16 lg:py-24 px-4 md:px-8 lg:px-[130px]',
        SECTION_BG[bg],
        className,
      )}
    >
      <div className="max-w-[1255px] mx-auto">{children}</div>
    </section>
  );
}

/** Pulsing-dot mono label used above headings across the marketing site. */
export function SectionLabel({
  children,
  tone = 'dark',
  className,
}: React.PropsWithChildren<{ tone?: 'dark' | 'light'; className?: string }>) {
  return (
    <div className={cn('flex items-center gap-3 mb-4 lg:mb-6', className)}>
      <div
        className={cn(
          'w-2 h-2 rounded-full',
          tone === 'light' ? 'bg-[#a9c8ff]' : 'bg-[#202020]',
        )}
        aria-hidden="true"
      />
      <p
        className={cn(
          'font-roboto-mono uppercase text-[12px] md:text-[14px] tracking-wide',
          tone === 'light' ? 'text-[#a9c8ff]' : 'text-[#202020]',
        )}
      >
        {children}
      </p>
    </div>
  );
}

/**
 * Dark ink hero for every marketing page. Sits under the fixed header.
 * With `visual`, it becomes the split layout used across the site: copy on
 * one side, an editorial photo with a live product demo on the other. With
 * `cta`, it adds the in-place demo scheduling button (and an optional
 * secondary link), so every page offers the same next step.
 */
export function PageHero({
  label,
  title,
  titleMuted,
  description,
  locale = 'en',
  visual,
  cta,
  children,
}: React.PropsWithChildren<{
  label: string;
  title: string;
  /** Optional second line rendered at 45% opacity, matching the landing hero. */
  titleMuted?: string;
  description?: string;
  locale?: MarketingLocale;
  visual?: { image: string; imageAlt: string; demo: DemoKey };
  cta?: { label: string; secondary?: { label: string; href: string } };
}>) {
  const copy = (
    <div>
      <SectionLabel tone="light">{label}</SectionLabel>
      <h1
        className={cn(
          'font-roboto tracking-tight',
          visual
            ? 'text-[34px] md:text-[48px] lg:text-[60px]'
            : 'text-[32px] md:text-[48px] lg:text-[64px]',
          // After the size classes: tailwind-merge drops a leading-* that
          // precedes a font-size utility.
          'leading-[1.06]',
        )}
        style={{ fontVariationSettings: "'wdth' 100" }}
      >
        {title}
        {titleMuted && <span className="mt-1 block text-white/45">{titleMuted}</span>}
      </h1>
      {description && (
        <p className="mt-6 max-w-[680px] font-roboto text-[16px] leading-relaxed text-white/65 md:text-[18px]">
          {description}
        </p>
      )}
      {cta && (
        <div className="mt-9 flex flex-wrap items-center gap-4">
          <ScheduleDemoButton label={cta.label} />
          {cta.secondary && (
            <Link
              href={cta.secondary.href}
              className="inline-flex h-[52px] items-center border border-white/15 px-7 font-roboto-mono text-[12px] uppercase transition-colors hover:bg-white/5 md:h-[56px] md:text-[14px]"
            >
              {cta.secondary.label}
            </Link>
          )}
        </div>
      )}
      {children}
    </div>
  );

  return (
    <section className="relative overflow-hidden bg-[#202020] px-4 pb-14 pt-[72px] text-white md:px-8 md:pb-20 md:pt-[96px] lg:px-[90px]">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-48 end-[-10%] h-[680px] w-[680px] rounded-full bg-[#a9c8ff]/[0.08] blur-[140px]"
      />
      <div
        className={cn(
          'relative mx-auto',
          visual
            ? 'grid max-w-[1320px] grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-14'
            : 'max-w-[1255px]',
        )}
      >
        {copy}
        {visual && (
          <AudienceVisual
            image={visual.image}
            imageAlt={visual.imageAlt}
            demo={visual.demo}
            locale={locale}
            priority
          />
        )}
      </div>
    </section>
  );
}

export function SectionHeading({
  children,
  className,
}: React.PropsWithChildren<{ className?: string }>) {
  return (
    <h2
      className={cn(
        'text-[26px] md:text-[32px] lg:text-[40px] font-roboto font-normal leading-[1.2] mb-6',
        className,
      )}
      style={{ fontVariationSettings: "'wdth' 100" }}
    >
      {children}
    </h2>
  );
}

/**
 * Full-width ink call to action closing every marketing page: the page's own
 * heading and pitch, then the shared new-venue / switching paths that open
 * the demo scheduler in place.
 */
export function CtaBand({
  heading,
  description,
  buttonLabel,
  isRTL = false,
}: {
  heading: string;
  description: string;
  buttonLabel?: string;
  isRTL?: boolean;
}) {
  return (
    <section
      id="demo"
      className="scroll-mt-16 bg-[#202020] px-4 py-16 md:px-8 md:py-24 lg:px-[90px]"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="mx-auto max-w-[1100px] text-center text-white">
        <h2
          className="font-roboto text-[30px] leading-[1.12] md:text-[40px] lg:text-[48px]"
          style={{ fontVariationSettings: "'wdth' 100" }}
        >
          {heading}
        </h2>
        <p className="mx-auto mt-4 max-w-[680px] font-roboto text-[16px] leading-relaxed text-white/65 md:text-[18px]">
          {description}
        </p>
        <div className="mt-12">
          <ScheduleDemoPaths locale={isRTL ? 'ar' : 'en'} buttonLabel={buttonLabel} />
        </div>
      </div>
    </section>
  );
}

export interface FaqEntryContent {
  question: string;
  answer: string;
}

/**
 * Native-details FAQ accordion. Server-renderable (no JS needed), so the
 * answers are always in the HTML for crawlers. Pair with FAQPage JSON-LD
 * via `buildFaqPageJsonLd`.
 */
export function FaqList({ entries }: { entries: FaqEntryContent[] }) {
  return (
    <div className="divide-y divide-[#dcd7cf] border-y border-[#dcd7cf]">
      {entries.map((entry) => (
        <details key={entry.question} className="group py-5">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-roboto text-[17px] md:text-[19px] text-[#202020] [&::-webkit-details-marker]:hidden">
            {entry.question}
            <span
              aria-hidden="true"
              className="shrink-0 text-[#8a857c] transition-transform duration-200 group-open:rotate-45 text-[22px] leading-none"
            >
              +
            </span>
          </summary>
          <p className="mt-3 max-w-[820px] font-roboto text-[15px] md:text-[16px] leading-relaxed text-[#2b2b2b]/80">
            {entry.answer}
          </p>
        </details>
      ))}
    </div>
  );
}
